import { Link } from 'react-router-dom'
import type { Call } from '@/shared/api/types'
import { OutcomeIcon, RecordingMark, ReplySpeed, ScoreRing, SentimentIcon } from './OutcomeIcon'
import styles from './CallRow.module.css'

interface CallRowProps {
  call: Call
  /** Сжатый вид: без полоски речи и скорости. Для узких колонок. */
  compact?: boolean
  /** Что показывать во времени: часы или дату со временем. */
  showDate?: boolean
  /** Название бота, если звонил не основной. */
  botName?: string
}

/**
 * Строка звонка.
 *
 * Одна на список и на обзор: две копии разъедутся на первой же правке,
 * и человек, привыкший читать список, на обзоре увидит другой порядок
 * колонок.
 */
export function CallRow({ call, compact = false, showDate = false, botName }: CallRowProps) {
  const report = call.report
  const marks = call.highlights ?? {}

  // Согласия и снятые возражения — в плюс, промахи и возражения — в
  // минус. Остальные виды в строке не показываем: пять значков подряд
  // читаются хуже двух.
  const good = (marks.agreement ?? 0) + (marks.handled ?? 0) + (marks.interest ?? 0)
  const bad = (marks.missed ?? 0) + (marks.objection ?? 0)

  return (
    <Link
      to={`/calls/${call.id}`}
      className={`${styles.row} ${compact ? styles.compact : ''}`}
      title={tooltip(call)}
    >
      <span className={styles.outcome}>
        <span className={`${styles.outcomeIcon} ${iconClass(call)}`}>
          <OutcomeIcon outcome={report?.outcome} />
        </span>
        <span className={styles.outcomeText}>
          {report?.outcomeLabel ?? (call.status === 'failed' ? 'Оборвался' : 'Разбираем…')}
        </span>
        {call.kind === 'test' && <span className={styles.tag}>тест</span>}
        {botName && <span className={styles.tag} title="Каким ботом шёл звонок">{botName}</span>}
      </span>

      <span className={styles.summary}>
        {report?.nextActionAt && (
          // Дата переноса впереди сводки: ради неё список и открывают.
          <span className={styles.next}>→ {shortDate(report.nextActionAt)} · </span>
        )}
        {report?.summary || call.error || 'Стенограмма без разбора'}
      </span>

      <span className={styles.marks}>
        {report && (
          <span className={styles.sentiment}>
            <SentimentIcon sentiment={report.sentiment} />
          </span>
        )}
        {good > 0 && (
          <span className={`${styles.mark} ${styles.markGood}`} title="удачных моментов">
            <CheckMark />
            {good}
          </span>
        )}
        {bad > 0 && (
          <span className={`${styles.mark} ${styles.markBad}`} title="возражений и промахов">
            <WarnMark />
            {bad}
          </span>
        )}
        <span className={styles.recording}>
          <RecordingMark call={call} />
        </span>
      </span>

      {!compact &&
        (call.talkRatio > 0 ? (
          <span className={styles.talk} title={talkTitle(call.talkRatio)}>
            <span className={styles.talkAgent} style={{ width: `${call.talkRatio * 100}%` }} />
            <span className={styles.talkHuman} style={{ width: `${(1 - call.talkRatio) * 100}%` }} />
          </span>
        ) : (
          <span className={styles.talkEmpty}>—</span>
        ))}

      {!compact && (
        <span className={styles.speed}>
          <ReplySpeed ms={call.medianReplyMs} />
        </span>
      )}

      <span className={styles.when}>
        {showDate
          ? shortDate(call.startedAt ?? call.createdAt)
          : time(call.startedAt ?? call.createdAt)}
      </span>

      <span className={styles.score}>
        {report ? (
          <ScoreRing score={report.score} success={report.success} />
        ) : (
          <span className={styles.scoreEmpty}>—</span>
        )}
      </span>
    </Link>
  )
}

/** Шапка таблицы. Стоит один раз сверху, а не в каждой строке. */
export function CallRowHead() {
  return (
    <div className={styles.head}>
      <span>Итог</span>
      <span>О чём говорили</span>
      <span>Моменты</span>
      <span>Кто говорил</span>
      <span>Ответ</span>
      <span>Время</span>
      <span />
    </div>
  )
}

/**
 * Подсказка при наведении.
 *
 * Значки экономят место, но первый раз их надо расшифровать. Подсказка
 * и есть эта расшифровка — потом её перестают вызывать.
 */
function tooltip(call: Call): string {
  const parts = [`Длительность ${duration(call.durationMs)}`, `${call.turnsCount} реплик`]

  if (call.bargeInCount > 0) parts.push(`${call.bargeInCount} перебиваний`)
  if (call.medianReplyMs > 0) parts.push(`ответ ${(call.medianReplyMs / 1000).toFixed(1)} с`)
  if (call.talkRatio > 0) parts.push(talkTitle(call.talkRatio))

  return parts.join(' · ')
}

function talkTitle(ratio: number): string {
  return `агент говорил ${Math.round(ratio * 100)}% времени`
}

function iconClass(call: Call): string {
  if (!call.report) return ''
  if (call.report.success) return styles.iconWin ?? ''
  if (call.report.outcome === 'refusal' || call.report.outcome === 'hangup') {
    return styles.iconLoss ?? ''
  }
  return styles.iconSoft ?? ''
}

function time(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

function shortDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function duration(ms: number): string {
  if (ms <= 0) return '—'
  const seconds = Math.round(ms / 1000)
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return `${mm}:${String(ss).padStart(2, '0')}`
}

function CheckMark() {
  return (
    <svg width="8" height="7" viewBox="0 0 10 8" fill="none" aria-hidden="true">
      <path d="M1 4.2L3.6 6.6L9 1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function WarnMark() {
  return (
    <svg width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 1.4V5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="5" cy="8" r="0.9" fill="currentColor" />
    </svg>
  )
}
